import 'uplot/dist/uPlot.min.css';

import {isEqual} from 'lodash';
import React, {
  type FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import type uPlot from 'uplot';
import UplotReact from 'uplot-react';

import type {HoveredIssueSource} from '../../../state/issues/hooks';
import {type IssueLine} from '../../../state/issues/types';
import {recordRumAction} from '../../../util/datadog/datadogRum';
import {type PlotFontSize} from '../../../util/plotHelpers/plotFontSize';
import {type PlotMargin} from '../../../util/plotHelpers/plotHelpers';
import {
  type Line,
  type LineType,
  type PlotType,
  type Timestep,
} from '../../../util/plotHelpers/types';
import {useUPlotMode} from '../../../util/rampFeatureFlags/linePlotDynamicConfigs';
import {useRampFlagUPlotUser} from '../../../util/rampFeatureFlags/rampFeatureFlags';
import {useColorMode} from '../../../util/useColorMode';
import {usePanelConfigContext} from '../../PanelRunsLinePlot/PanelConfigContext';
import {useUpdateIconPositions} from '../LinePlotPlot';
import {type DomainMaybe} from '../types';
import {type IssueLinesConfig} from './drawIssueLines';
import {type NonFiniteMarkerState} from './drawNonFiniteMarkers';
import {type StackedAreaDrawState} from './drawStackedAreas';
import {getUPlotOptions} from './getUPlotOptions';
import {getuPlotMarginOverride} from './margins';
import {type PreparedUPlotData, prepareUPlotData} from './prepareUPlotData';
import {useAlignmentProfile} from './useAlignmentProfile';
import {useReactVisUsageTracking} from './useReactVisUsageTracking';

const SUPPORTED_LINE_TYPES: Set<LineType> = new Set(['line', 'area']);

export function useShouldUseuPlot(
  lines: Line[],
  plotType: PlotType | undefined,
  panelId: string
) {
  const uplotEnabled = useRampFlagUPlotUser() === true;
  const firstUnsupportedLine = useMemo(
    () =>
      lines.find(
        line => line.type == null || !SUPPORTED_LINE_TYPES.has(line.type)
      ),
    [lines]
  );
  const areAllLinesuPlotSupported = firstUnsupportedLine == null;
  const uPlotUnsupportedLineType =
    firstUnsupportedLine == null
      ? undefined
      : (firstUnsupportedLine.type ?? 'missing');
  const isChartTypeuPlotSupported =
    plotType === 'line' ||
    plotType === 'stacked-area' ||
    plotType === 'pct-area';
  const panelConfig = usePanelConfigContext();
  const forceReactVis = panelConfig.forceReactVis === true;
  const shouldUseuPlot =
    uplotEnabled &&
    areAllLinesuPlotSupported &&
    isChartTypeuPlotSupported &&
    !forceReactVis;
  useReactVisUsageTracking(
    shouldUseuPlot,
    {
      areAllLinesuPlotSupported,
      forceReactVis,
      isChartTypeuPlotSupported,
      plotType,
      uplotEnabled,
      uPlotUnsupportedLineType,
    },
    panelId
  );
  return shouldUseuPlot;
}

const emptyIssueLines: IssueLine[] = [];

/** Let marker drawing read the latest dimming and hover state without rebuilding the chart. */
function useNonFiniteMarkerState(
  dimFactor: number | undefined,
  uplotRef: React.RefObject<uPlot | null>
) {
  const stateRef = useRef<NonFiniteMarkerState>({
    hoveredMarkerIndex: -1,
    dimFactor,
  });
  const getState = useCallback(() => stateRef.current, []);
  useLayoutEffect(() => {
    if (stateRef.current.dimFactor === dimFactor) {
      return;
    }
    stateRef.current.dimFactor = dimFactor;
    uplotRef.current?.redraw(false);
  }, [dimFactor, uplotRef]);
  return getState;
}

/**
 * Let the stacked-area hook read the hovered runs without rebuilding the chart.
 * The highlight effect's `setSeries` calls trigger the redraw, and layout
 * effects run first, so that redraw sees the new runs.
 */
function useStackedAreaDrawState(
  highlightedLines: ReadonlySet<Line> | undefined
) {
  const stateRef = useRef<StackedAreaDrawState>({highlightedLines});
  const getState = useCallback(() => stateRef.current, []);
  useLayoutEffect(() => {
    stateRef.current.highlightedLines = highlightedLines;
  }, [highlightedLines]);
  return getState;
}

function getNoHoveredIssue(): null {
  return null;
}

/** Redraws the chart when the hovered issue changes, without re-rendering. */
function useRedrawOnHoveredIssueChange(
  source: HoveredIssueSource | undefined,
  uplotRef: React.RefObject<uPlot | null>
) {
  useEffect(
    function subscribeToHoveredIssue() {
      if (source == null) {
        return undefined;
      }
      return source.subscribe(function redrawForHoveredIssue() {
        uplotRef.current?.redraw(false);
      });
    },
    [source, uplotRef]
  );
}

type LinePlotuPlotProps = {
  lines: Line[];
  /** Stable panel ID, sent with telemetry. */
  panelId: string;
  /** Prepared line and band points shared by the main and highlight layers. Charts only read them. */
  preparedData?: PreparedUPlotData;
  width: number;
  height: number;
  xDomain: DomainMaybe;
  yDomain: DomainMaybe;
  fontSize: PlotFontSize;
  timestep: Timestep | undefined;
  /** Issue lines to render on the chart */
  issueLines?: IssueLine[];
  /**
   * Hovered issue for highlighting issue lines. Read when drawing, so a hover
   * change redraws the chart instead of rebuilding it. Must be stable.
   */
  hoveredIssueSource?: HoveredIssueSource;
  /** Whether the x-axis min is zoomed (if true, hide 'before' lines) */
  isZoomedXMin?: boolean;
  /** Whether the x-axis max is zoomed (if true, hide 'after' lines) */
  isZoomedXMax?: boolean;
  /** If true, this is the overlay layer that contains the highlighted lines */
  isOverlay?: boolean;
  /** If true, this is the base layer that draws the grid */
  isBaseLayer?: boolean;
  /**
   * Lines to draw at full brightness in the highlight layer.
   * Show or hide these lines on the existing chart so hovering does not rebuild it.
   */
  highlightedLines?: ReadonlySet<Line>;
  dimFactor?: number;
  /** Use to override the default react-vis plot margin in the crosshair component */
  plotMarginOverride: PlotMargin | undefined;
  setPlotMarginOverride?: (plotMargin: PlotMargin) => void;
};

export const LinePlotuPlot: FC<LinePlotuPlotProps> = ({
  lines,
  panelId: panelID,
  preparedData,
  width,
  height,
  xDomain,
  yDomain,
  fontSize,
  timestep,
  issueLines = emptyIssueLines,
  hoveredIssueSource,
  isZoomedXMin = false,
  isZoomedXMax = false,
  isOverlay = false,
  isBaseLayer = false,
  highlightedLines,
  dimFactor,
  plotMarginOverride,
  setPlotMarginOverride,
}) => {
  const uPlotMode = useUPlotMode();

  const {xAxisIsDatetime, xLogScale, yLogScale} = usePanelConfigContext();
  // update the issues line icon position to match the plot margin
  useUpdateIconPositions(xDomain, plotMarginOverride);

  const seriesEntriesResult = useMemo(
    function prepareLayerData() {
      return preparedData ?? prepareUPlotData(lines, xLogScale, uPlotMode);
    },
    [preparedData, lines, xLogScale, uPlotMode]
  );
  useAlignmentProfile(
    panelID,
    preparedData == null ? seriesEntriesResult.alignmentMeasurement : undefined
  );
  const {data} = seriesEntriesResult;
  const uplotRef = useRef<uPlot | null>(null);

  const getNonFiniteMarkerState = useNonFiniteMarkerState(dimFactor, uplotRef);
  const getStackedAreaDrawState = useStackedAreaDrawState(highlightedLines);

  // Only charts that draw issue lines read or follow the hovered issue.
  const drawnHoveredIssueSource =
    issueLines.length > 0 ? hoveredIssueSource : undefined;
  useRedrawOnHoveredIssueChange(drawnHoveredIssueSource, uplotRef);
  const getHoveredIssue = drawnHoveredIssueSource?.get ?? getNoHoveredIssue;

  const issueLinesConfig: IssueLinesConfig = useMemo(
    () => ({
      issueLines,
      getHoveredIssue,
      isZoomedXMin,
      isZoomedXMax,
    }),
    [issueLines, getHoveredIssue, isZoomedXMin, isZoomedXMax]
  );

  const {isDarkMode} = useColorMode();

  /**
   * Reuse the drawing settings until the data or chart settings change.
   * Marker, stacked-area, and issue-line drawing read changing hover and
   * dimming state through getters, so those changes do not rebuild the chart.
   */
  const options = useMemo(
    () =>
      getUPlotOptions(
        seriesEntriesResult.seriesEntries,
        seriesEntriesResult.lineNameMap,
        lines,
        getNonFiniteMarkerState,
        issueLinesConfig,
        width,
        height,
        xDomain,
        yDomain,
        xAxisIsDatetime,
        xLogScale,
        yLogScale,
        fontSize,
        timestep,
        isDarkMode,
        isOverlay,
        isBaseLayer,
        seriesEntriesResult.uPlotMode,
        getStackedAreaDrawState
      ),
    [
      seriesEntriesResult,
      getNonFiniteMarkerState,
      getStackedAreaDrawState,
      lines,
      issueLinesConfig,
      width,
      height,
      xDomain,
      yDomain,
      xAxisIsDatetime,
      xLogScale,
      yLogScale,
      fontSize,
      timestep,
      isDarkMode,
      isOverlay,
      isBaseLayer,
    ]
  );
  const lastMarginRef = useRef<PlotMargin | null>(null);

  // Keep the crosshair aligned with the chart's actual drawing area after rendering.
  useEffect(() => {
    // Wait until the chart is ready; its drawing area is not accurate during setup.
    const u = uplotRef.current;
    if (setPlotMarginOverride == null || u == null || u.status !== 1) {
      return;
    }
    const margin = getuPlotMarginOverride(u.width, u.height, u.bbox);
    if (margin != null && !isEqual(margin, lastMarginRef.current)) {
      lastMarginRef.current = margin;
      setPlotMarginOverride(margin);
    }
    // Recheck after each render so resizing stays aligned; publish only changed margins.
  });

  // Show the selected lines in the highlight layer without rebuilding the chart.
  // Only change lines whose visibility differs, so moving along the same run
  // does not repeatedly update them.
  useEffect(() => {
    const u = uplotRef.current;
    if (u == null || highlightedLines == null) {
      return;
    }
    const {seriesEntries} = seriesEntriesResult;
    for (let i = 1; i < seriesEntries.length; i++) {
      const entry = seriesEntries[i];
      if (entry == null) {
        continue;
      }
      const shouldShow = highlightedLines.has(entry.line);
      if (u.series[i]?.show !== shouldShow) {
        u.setSeries(i, {show: shouldShow});
      }
    }
  }, [highlightedLines, seriesEntriesResult]);

  const smoothedFromBackend = lines.some(
    l => l.smoothedLineFromBackend != null
  );

  const hasSmoothedLines = smoothedFromBackend || lines.some(l => l.smoothed);

  useEffect(() => {
    if (hasSmoothedLines) {
      recordRumAction('line_plot_smoothed', {
        panelID,
        smoothedFromBackend,
        uplot: true,
      });
    }
  }, [panelID, hasSmoothedLines, smoothedFromBackend]);

  return (
    <div
      className="w-full h-full relative"
      data-test="line-plot-uplot"
      data-smoothed-from-backend={smoothedFromBackend}>
      <UplotReact
        // Start a new chart when the X-value layout or number of lines/band edges
        // changes. The wrapper otherwise applies new settings to old data first,
        // which can fail because the chart expects a different data shape.
        key={`${seriesEntriesResult.uPlotMode}:${seriesEntriesResult.seriesEntries.length}`}
        options={options}
        // The wrapper accepts both X-value layouts, but its types describe only shared X values.
        data={data as uPlot.AlignedData}
        onCreate={u => {
          uplotRef.current = u;
        }}
        onDelete={u => {
          if (uplotRef.current === u) {
            uplotRef.current = null;
          }
        }}
      />
      {/* Axis titles are rendered by the parent (LinePlot) in reserved strips
          outside the plot, so they no longer overlap the chart. */}
    </div>
  );
};
